import { useRef, useState } from 'react';
import { loadRecording, type LoadedRecording } from '../lib/loadRecording';
import { navigate } from '../lib/router';
import { inflateAvailable } from '../lib/unzip';

/**
 * "Load a round" — the front door of every mode that works off an export
 * rather than off a live session: the playback on the beamer (2026-09-09)
 * and the vertical clip renderer's preview.
 *
 * Nothing leaves the device. The zip is opened in the browser, which is why
 * a round stays playable on any laptop long after its session is gone.
 */
export function RecordingLoader(props: {
  title: string;
  lead: React.ReactNode;
  hint?: React.ReactNode;
  onLoaded: (loaded: LoadedRecording) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const take = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      props.onLoaded(await loadRecording([...files]));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Die Aufnahme konnte nicht gelesen werden.');
      setBusy(false);
    }
  };

  return (
    <div className="home">
      <header className="masthead masthead--small">
        <h1>{props.title}</h1>
        <p className="masthead__meta">{props.lead}</p>
      </header>

      <div className="home__grid home__grid--play">
        <section className="card card--wide">
          <span className="card__no">Aufnahme</span>
          <h2>Runden-Export laden</h2>
          <p>
            Das Zip, das die Moderation nach der Runde heruntergeladen hat — oder die Dateien daraus. Nichts wird
            hochgeladen: die Aufnahme wird hier im Browser gelesen.
          </p>
          <div
            className={`dropzone ${dragging ? 'dropzone--over' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              void take(e.dataTransfer.files);
            }}
            onClick={() => inputRef.current?.click()}
            data-testid="playback-dropzone"
          >
            <strong>{busy ? 'Wird gelesen…' : 'Datei hierher ziehen'}</strong>
            <span>oder klicken und auswählen</span>
            <input
              ref={inputRef}
              className="dropzone__input"
              type="file"
              multiple
              accept=".zip,.json,.jpg,.jpeg,.png,.webp,application/zip,application/json,image/*"
              onChange={(e) => void take(e.target.files)}
              data-testid="playback-file"
            />
          </div>
          {error && (
            <p className="form-error" data-testid="playback-error">
              {error}
            </p>
          )}
          <ul className="playhint">
            {props.hint ?? (
              <>
                <li>
                  <code>schreibarena-…-runde-N.zip</code> — alles drin, der einfache Weg.
                </li>
                <li>
                  <code>…-keystrokes.json</code> — Text, Züge und beide Uhren, Anschlag für Anschlag.
                </li>
                <li>
                  <code>…-story.json</code> — dazu die Moderationsspur: Joker, Strafen, Pausen.
                </li>
                <li>Bildrunde? Das Comicbild mit auswählen — im Zip ist es schon dabei.</li>
              </>
            )}
          </ul>
          {!inflateAvailable() && (
            <p className="form-error">
              Dieser Browser kann keine Zip-Dateien entpacken. Bitte die JSON-Dateien einzeln auswählen.
            </p>
          )}
        </section>
      </div>

      <footer className="home__foot">
        <button className="btn btn--ghost btn--small" onClick={() => navigate('#/')}>
          ← Zurück
        </button>
        <span>Gelöscht wird nicht — auch nicht im Nachhinein.</span>
      </footer>
    </div>
  );
}
